import {useQuery} from 'react-query';
import {TopicVO, ServicesApi, TopicVisibility, TopicsResponseVO} from '../api';
import {useMapioContext} from '../mapioContext';

export const CACHE_KEY_TOPICS_MAP = 'CACHE_KEY_TOPICS_MAP';

const EMPTY_TOPICS: TopicVO[] = [];

// Selektory musí být referenčně stabilní, jinak si je react-query pouští znovu při každém renderu
// a vrací novou referenci dat (= zbytečné překreslení všech konzumentů).
const selectTopics = (data: TopicsResponseVO): TopicVO[] => data.topics ?? EMPTY_TOPICS;
const selectAtlasPristupnosti = (data: TopicsResponseVO): boolean => !!data.atlasPristupnosti;

/**
 * Dotaz na /topics-map. Vrací celou odpověď, konkrétní část si každý konzument vybere přes `select`
 * – všechna volání tak sdílí jednu cache i jeden request.
 */
const useTopicsMapResponse = <TData,>(select: (data: TopicsResponseVO) => TData, topicVisibility?: TopicVisibility) => {
    const context = useMapioContext();
    const value = context?.value;

    const resolvedVisibility = topicVisibility ?? (value?.isIframe ? TopicVisibility.APLIKACE : TopicVisibility.VSE);

    return useQuery<TopicsResponseVO, unknown, TData>(
        [CACHE_KEY_TOPICS_MAP, resolvedVisibility, value?.context],
        async () => (await ServicesApi.getTopicsMap(value.context, resolvedVisibility)).data,
        {
            select,
            enabled: !!value && value.isIframe !== undefined && value.isIframe !== null && resolvedVisibility !== null,
            // Konfigurace témat je v rámci session statická – refetch na focus okna po nečinnosti
            // způsoboval reset viditelnosti vrstev (MCW: znovuobjevení vypnutých vrstev v mapě).
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        },
    );
};

export const useTopicsMap = (topicVisibility?: TopicVisibility) => useTopicsMapResponse(selectTopics, topicVisibility);

/**
 * Příznak organizace „Atlas přístupnosti“ (MCW-29581). Dokud není zapnutý, uživatelská konfigurace
 * skrývá vše, co je určeno jen pro atlas přístupnosti. Do načtení odpovědi vracíme false,
 * aby se dané položky nestihly problikávat.
 */
export const useAtlasPristupnosti = (): boolean => useTopicsMapResponse(selectAtlasPristupnosti).data ?? false;
